/**
 * DICOM Storage — Dexie edition (Merged Ultimate + Enterprise v8)
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 *
 * MERGED: v5 (full API) + v6 (OOM-safe) + v7 (True LRU, Tags, Integrity) + v8 (Enterprise)
 *
 * v3 — базовая схема (studies, files, thumbnails, settings)
 * v4 — qualityParams
 * v5 — annotations + userPrefs + legacy migration
 * v6 — viewState store
 * v7 — lastAccessed (True LRU), *tags multi-entry index, integrity checks
 * v8 — favourites flag, protectedStudy (pin), studyNotes, auditLog table,
 *       compound [patientId+studyDate], batch integrity repair, vacuum,
 *       selective export (annotations-only / viewStates-only), bulk tagging,
 *       storage quota watchdog, full-text patient search via trigrams,
 *       study duplication, study merge, per-series file count, DICOM dir,
 *       background compaction, configurable eviction policies, stats history
 *
 * [ARCHITECTURE]
 * - Zero-OOM Base64: FileReader API (encode) + Fetch API (decode)
 * - Sequential ArrayBuffer unwrapping (flat RAM, no parallel spikes)
 * - QuotaExceeded auto-recovery with configurable eviction policy
 * - True LRU eviction по lastAccessed (не по savedAt)
 * - Compound index fast-path для поиска по modality+date, patientId+date
 * - Study integrity verification & auto-repair
 * - AuditLog for every destructive action
 * - yieldThread() между батчами для 60fps UI
 * - Configurable storage limits via settings store
 * - Quota watchdog with EventTarget notifications
 */

import Dexie from 'dexie';
import { StudyQualityAnalyzer } from './studyQualityAnalyzer.js';
import { log } from '../utils/logger.js';

// ══════════════════════════════════════════════════════════════
// DB CONFIG
// ══════════════════════════════════════════════════════════════

const DB_NAME = 'MikhmelDICOM';
const CURRENT_DB_VERSION = 8;
const EXPORT_FORMAT_VERSION = 3;
const STORAGE_LIMIT_PERCENT = 80;
const MAX_STUDIES = 50;
const SAVE_BATCH_SIZE = 30;
const AUDIT_MAX_ENTRIES = 500;
const QUOTA_CHECK_INTERVAL_MS = 60_000;

/** Разблокировка главного потока между тяжёлыми операциями */
const yieldThread = () => new Promise(resolve => setTimeout(resolve, 0));

// ══════════════════════════════════════════════════════════════
// EVICTION POLICIES
// ══════════════════════════════════════════════════════════════

const EVICTION_POLICIES = {
  /** True LRU — удаляет по lastAccessed (default) */
  lru: (studies, n) => studies.sort((a, b) => (a.lastAccessed || 0) - (b.lastAccessed || 0)).slice(0, n),
  /** Oldest first — по дате сохранения */
  oldest: (studies, n) => studies.sort((a, b) => (a.savedAt || '').localeCompare(b.savedAt || '')).slice(0, n),
  /** Largest first — самые тяжёлые */
  largest: (studies, n) => studies.sort((a, b) => (b.sizeBytes || 0) - (a.sizeBytes || 0)).slice(0, n),
  /** Protected-aware LRU — protected никогда не удаляются */
  protectedLru: (studies, n) => {
    const unprotected = studies.filter(s => !s.protectedStudy);
    return unprotected.sort((a, b) => (a.lastAccessed || 0) - (b.lastAccessed || 0)).slice(0, n);
  },
};

let _evictionPolicy = 'protectedLru';

// ══════════════════════════════════════════════════════════════
// DB SCHEMA & MIGRATIONS
// ══════════════════════════════════════════════════════════════

function createDB() {
  const db = new Dexie(DB_NAME);

  // v3 + v4: базовая схема + qualityParams (объединены — нет изменений индексов)
  db.version(4).stores({
    studies: 'id, patientName, patientId, studyDate, modality, savedAt, [modality+studyDate]',
    files: '++id, studyId, name',
    thumbnails: 'seriesUID',
    settings: 'key',
  });

  // v5: annotations + userPrefs
  db.version(5).stores({
    annotations: 'seriesUID, studyId, savedAt',
    userPrefs: 'key',
  }).upgrade(async tx => {
    log.debug('[DicomStore] Migrating v4 → v5...');
    await _migrateLegacyAnnotations(tx);
    await tx.table('userPrefs').bulkPut([
      { key: 'profile', value: 'general' },
      { key: 'dbVersion', value: 5 },
      { key: 'migratedAt', value: new Date().toISOString() },
    ]);
  });

  // v6: viewState (W/L, layout per series)
  db.version(6).stores({
    viewState: 'seriesUID',
  }).upgrade(async tx => {
    log.debug('[DicomStore] Migrating v5 → v6...');
    await tx.table('userPrefs').put({ key: 'dbVersion', value: 6 });
  });

  // v7: True LRU (lastAccessed index) + tags (multi-entry)
  db.version(7).stores({
    studies: 'id, patientName, patientId, studyDate, modality, savedAt, lastAccessed, *tags, [modality+studyDate]',
  }).upgrade(async tx => {
    log.debug('[DicomStore] Migrating v6 → v7 (LRU + Tags)...');
    await tx.table('studies').toCollection().modify(study => {
      study.lastAccessed = study.lastAccessed || Date.now();
      study.tags = study.tags || [];
    });
    await tx.table('userPrefs').put({ key: 'dbVersion', value: 7 });
  });

  // v8: auditLog, protected, favourites, notes, patientId+studyDate compound
  db.version(8).stores({
    studies: 'id, patientName, patientId, studyDate, modality, savedAt, lastAccessed, *tags, [modality+studyDate], [patientId+studyDate], favourite, protectedStudy',
    auditLog: '++id, action, timestamp, studyId',
  }).upgrade(async tx => {
    log.debug('[DicomStore] Migrating v7 → v8 (Enterprise)...');
    await tx.table('studies').toCollection().modify(study => {
      study.favourite = study.favourite || false;
      study.protectedStudy = study.protectedStudy || false;
      study.notes = study.notes || '';
    });
    await tx.table('userPrefs').put({ key: 'dbVersion', value: 8 });
  });

  return db;
}

// ══════════════════════════════════════════════════════════════
// LEGACY MIGRATION
// ══════════════════════════════════════════════════════════════

async function _migrateLegacyAnnotations(tx) {
  const LEGACY_DB_NAME = 'MikhmelAnnotations';
  const LEGACY_STORE = 'ann_store';

  try {
    const dbs = await (indexedDB.databases ? indexedDB.databases() : Promise.resolve([]));
    if (!dbs.some(d => d.name === LEGACY_DB_NAME)) return;

    return new Promise((resolve) => {
      const req = indexedDB.open(LEGACY_DB_NAME, 1);
      req.onerror = () => resolve();
      req.onsuccess = () => {
        const legacyDB = req.result;
        if (!legacyDB.objectStoreNames.contains(LEGACY_STORE)) {
          legacyDB.close(); return resolve();
        }
        const store = legacyDB.transaction(LEGACY_STORE, 'readonly').objectStore(LEGACY_STORE);
        const getAll = store.getAll();

        getAll.onsuccess = async () => {
          const records = getAll.result || [];
          let migrated = 0;
          for (const rec of records) {
            if (rec.key && rec.data) {
              try {
                await tx.table('annotations').put({
                  seriesUID: rec.key,
                  studyId: rec.studyId || null,
                  data: rec.data,
                  savedAt: rec.savedAt || Date.now(),
                  version: 1,
                  migratedFrom: 'MikhmelAnnotations',
                });
                migrated++;
              } catch (e) { log.debug('[DicomStore] skip record:', e.message); }
            }
          }
          legacyDB.close();
          log.debug(`[DicomStore] Migrated ${migrated}/${records.length} legacy annotations`);
          try { indexedDB.deleteDatabase(LEGACY_DB_NAME); } catch (_) { /* non-critical */ }
          resolve();
        };
        getAll.onerror = () => { legacyDB.close(); resolve(); };
      };
    });
  } catch (e) {
    log.warn('[DicomStore] Legacy migration skipped:', e.message);
  }
}

// ══════════════════════════════════════════════════════════════
// DB INIT (Auto-Recovery)
// ══════════════════════════════════════════════════════════════

let db = createDB();

async function ensureDB() {
  try {
    await db.open();
    log.debug(`[DicomStore] DB opened, v${db.verno}`);
  } catch (e) {
    if (e.name === 'UpgradeError' || e.name === 'VersionError' || e.name === 'DatabaseClosedError' ||
        (e.inner && e.inner.name === 'UpgradeError')) {
      log.warn('[DicomStore] Schema corruption, auto-recovery:', e.message);
      if (db.isOpen()) db.close();
      await Dexie.delete(DB_NAME);
      db = createDB();
      await db.open();
      log.warn('[DicomStore] DB recreated (cache cleared)');
    } else {
      throw e;
    }
  }
}

const _ready = ensureDB();
async function ready() { await _ready; return db; }

// ══════════════════════════════════════════════════════════════
// AUDIT LOG (v8)
// ══════════════════════════════════════════════════════════════

async function _audit(action, studyId = null, details = null) {
  try {
    await db.auditLog.add({
      action,
      studyId,
      details,
      timestamp: Date.now(),
      iso: new Date().toISOString(),
    });
    // Auto-trim
    const count = await db.auditLog.count();
    if (count > AUDIT_MAX_ENTRIES) {
      const oldest = await db.auditLog.orderBy('id').limit(count - AUDIT_MAX_ENTRIES).primaryKeys();
      await db.auditLog.bulkDelete(oldest);
    }
  } catch (e) { log.debug('[DicomStore] audit write:', e.message); }
}

/**
 * [v8] Получить лог аудита (последние N записей)
 */
export async function getAuditLog(limit = 50) {
  try {
    await ready();
    return db.auditLog.orderBy('id').reverse().limit(limit).toArray();
  } catch (e) { log.warn(`[DicomStore] getAuditLog failed:`, e.message); return []; }
}

/**
 * [v8] Очистить лог аудита
 */
export async function clearAuditLog() {
  try {
    await ready();
    await db.auditLog.clear();
  } catch (e) { log.warn(`[DicomStore] clearAuditLog failed:`, e.message);  }
}

/**
 * [v8] Поиск в аудит-логе по действию
 */
export async function searchAuditLog(action, limit = 50) {
  try {
    await ready();
    return db.auditLog.where('action').equals(action).reverse().limit(limit).toArray();
  } catch (e) { log.warn(`[DicomStore] searchAuditLog failed:`, e.message); return []; }
}

// ══════════════════════════════════════════════════════════════
// DB INFO
// ══════════════════════════════════════════════════════════════

export const getDBVersion = () => CURRENT_DB_VERSION;
export const getExportFormatVersion = () => EXPORT_FORMAT_VERSION;

export async function getDBInfo() {
  try {
    await ready();
    const tables = db.tables.map(t => t.name);
    let userPrefs = {};
    try {
      const prefs = await db.userPrefs.toArray();
      for (const p of prefs) userPrefs[p.key] = p.value;
    } catch (e) { log.debug('[DicomStore] getDBInfo prefs:', e.message); }
  
    let tableSizes = {};
    for (const t of db.tables) {
      try { tableSizes[t.name] = await t.count(); } catch { tableSizes[t.name] = -1; }
    }
  
    return {
      name: DB_NAME,
      version: db.verno,
      currentVersion: CURRENT_DB_VERSION,
      tables,
      tableSizes,
      userPrefs,
      evictionPolicy: _evictionPolicy,
    };
  } catch (e) { log.warn(`[DicomStore] getDBInfo failed:`, e.message); return {}; }
}

// ══════════════════════════════════════════════════════════════
// CORE: SAVE / GET / LIST / DELETE / CLEAR
// ══════════════════════════════════════════════════════════════

export async function saveStudy(studyMeta, files = [], onProgress) {
  try {
    await ready();
  
    const meta = {
      ...studyMeta,
      savedAt: new Date().toISOString(),
      lastAccessed: Date.now(),
      fileCount: files.length,
      sizeBytes: files.reduce((s, f) => s + (f?.size || 0), 0),
      tags: studyMeta.tags || [],
      favourite: studyMeta.favourite || false,
      protectedStudy: studyMeta.protectedStudy || false,
      notes: studyMeta.notes || '',
    };
  
    if (files.length > 0 && !meta.qualityParams) {
      try { meta.qualityParams = await _extractQualityFromFile(files[0]); }
      catch (e) { log.warn('[DicomStore] Quality extraction failed:', e.message); }
    }
  
    try {
      await db.studies.put(meta);
      _audit('saveStudy', meta.id, { fileCount: files.length, sizeBytes: meta.sizeBytes });
      if (!files.length) return meta;
  
      await db.files.where('studyId').equals(studyMeta.id).delete();
  
      // Последовательная загрузка ArrayBuffer — flat RAM (не параллельная)
      let saved = 0;
      for (let i = 0; i < files.length; i += SAVE_BATCH_SIZE) {
        const batch = files.slice(i, i + SAVE_BATCH_SIZE);
        const records = [];
        for (let j = 0; j < batch.length; j++) {
          records.push({
            studyId: studyMeta.id,
            name: batch[j].name || `file_${i + j}.dcm`,
            data: await batch[j].arrayBuffer(),
          });
        }
        await db.files.bulkAdd(records);
        saved += batch.length;
        if (onProgress) onProgress(saved, files.length);
        await yieldThread();
      }
    } catch (error) {
      // [v6] QuotaExceeded auto-recovery
      if (error.name === 'QuotaExceededError' || error.message?.includes('Quota')) {
        log.warn('[DicomStore] Quota exceeded! Emergency cleanup...');
        _audit('quotaExceeded', studyMeta.id);
        await _autoCleanup(true);
        throw new Error('Недостаточно места в памяти браузера. Удалены старые исследования. Попробуйте снова.');
      }
      throw error;
    }
  
    _autoCleanup().catch(e => log.error('[DicomStore] Auto-cleanup failed:', e));
    return meta;
  } catch (e) { log.warn(`[DicomStore] saveStudy failed:`, e.message); throw e; }
}

export async function getStudy(studyId) {
  try {
    await ready();
    const study = await db.studies.get(studyId);
    if (study) {
      // [v7] True LRU: обновляем время доступа
      db.studies.update(studyId, { lastAccessed: Date.now() }).catch(() => {});
    }
    return study;
  } catch (e) { log.warn(`[DicomStore] getStudy failed:`, e.message); return null; }
}

export async function getStudies() {
  await ready();
  try {
    // [v7] Сортировка по последнему доступу (недавние первые)
    return await db.studies.orderBy('lastAccessed').reverse().toArray();
  } catch (e) {
    // Fallback для старых записей без lastAccessed
    const all = await db.studies.toArray();
    return all.sort((a, b) => (b.lastAccessed || 0) - (a.lastAccessed || 0) || (b.savedAt || '').localeCompare(a.savedAt || ''));
  }
}

export async function loadStudyFiles(studyId, onProgress) {
  try {
    await ready();
    // [v7] Touch LRU
    db.studies.update(studyId, { lastAccessed: Date.now() }).catch(() => {});
  
    const records = await db.files.where('studyId').equals(studyId).toArray();
    return records.map((r, i) => {
      if (onProgress) onProgress(i + 1, records.length);
      return new File([r.data], r.name || `file_${i}.dcm`, { type: 'application/dicom' });
    });
  } catch (e) { log.warn(`[DicomStore] loadStudyFiles failed:`, e.message); return null; }
}

/**
 * Поиск с compound index fast-path + tags + True LRU сортировка
 */
export async function searchStudies({ patientName, patientId, modality, dateFrom, dateTo, tags, favourite, protectedOnly, notes } = {}) {
  try {
    await ready();
  
    // [v8] Fast-path: compound index [patientId+studyDate]
    if (patientId && !patientName && !modality && !tags?.length && dateFrom) {
      try {
        const results = await db.studies
          .where('[patientId+studyDate]')
          .between([patientId, dateFrom], [patientId, dateTo || '\uffff'])
          .reverse().toArray();
        if (results.length) return results;
      } catch (e) { log.debug('[DicomStore] PatientId compound fallback:', e.message); }
    }
  
    // [v6] Fast-path: compound index [modality+studyDate]
    if (modality && !patientName && !patientId && !tags?.length && dateFrom) {
      try {
        return await db.studies
          .where('[modality+studyDate]')
          .between([modality.toUpperCase(), dateFrom], [modality.toUpperCase(), dateTo || '\uffff'])
          .reverse().toArray();
      } catch (e) { log.debug('[DicomStore] Compound index fallback:', e.message); }
    }
  
    // Lazy cursor filter (O(1) memory)
    let collection = db.studies.toCollection();
  
    if (patientName) {
      const q = patientName.toLowerCase();
      collection = collection.filter(s => (s.patientName || '').toLowerCase().includes(q));
    }
    if (patientId) collection = collection.filter(s => (s.patientId || '').includes(patientId));
    if (modality) {
      const m = modality.toUpperCase();
      collection = collection.filter(s => (s.modality || '').toUpperCase().includes(m));
    }
    if (dateFrom) collection = collection.filter(s => s.studyDate >= dateFrom);
    if (dateTo) collection = collection.filter(s => s.studyDate <= dateTo);
    // [v7] Фильтр по тегам
    if (tags?.length) {
      collection = collection.filter(s => tags.every(t => (s.tags || []).includes(t)));
    }
    // [v8] Фильтр по избранным
    if (favourite) {
      collection = collection.filter(s => !!s.favourite);
    }
    // [v8] Фильтр по защищённым
    if (protectedOnly) {
      collection = collection.filter(s => !!s.protectedStudy);
    }
    // [v8] Поиск по заметкам
    if (notes) {
      const q = notes.toLowerCase();
      collection = collection.filter(s => (s.notes || '').toLowerCase().includes(q));
    }
  
    const results = await collection.toArray();
    // [v7] Сортировка по последнему доступу
    return results.sort((a, b) => (b.lastAccessed || 0) - (a.lastAccessed || 0));
  } catch (e) { log.warn(`[DicomStore] searchStudies failed:`, e.message); return null; }
}

export async function deleteStudy(studyId) {
  try {
    await ready();
    await db.transaction('rw', db.studies, db.files, db.thumbnails, db.annotations, db.viewState, async () => {
      const study = await db.studies.get(studyId);
      if (!study) return;
  
      // [v8] Protected studies — refuse delete unless forced
      if (study.protectedStudy) {
        throw new Error(`Study "${study.patientName}" is protected. Unprotect first.`);
      }
  
      await db.studies.delete(studyId);
      await db.files.where('studyId').equals(studyId).delete();
  
      // [v7] Batch delete через anyOf (1 запрос вместо цикла)
      const uids = study.seriesUIDs || [];
      if (uids.length) {
        await db.thumbnails.where('seriesUID').anyOf(uids).delete().catch(e => log.debug('[DicomStore] thumbnails cleanup:', e.message));
        await db.annotations.where('seriesUID').anyOf(uids).delete().catch(e => log.debug('[DicomStore] annotations cleanup:', e.message));
        await db.viewState.where('seriesUID').anyOf(uids).delete().catch(e => log.debug('[DicomStore] viewState cleanup:', e.message));
      }
    });
    _audit('deleteStudy', studyId);
  } catch (e) { log.warn(`[DicomStore] deleteStudy failed:`, e.message); throw e; }
}

/**
 * [v8] Принудительное удаление (игнорирует protected)
 */
export async function forceDeleteStudy(studyId) {
  try {
    await ready();
    // Temporarily unprotect, then delete
    await db.studies.update(studyId, { protectedStudy: false }).catch(() => {});
    await deleteStudy(studyId);
    _audit('forceDeleteStudy', studyId);
  } catch (e) { log.warn(`[DicomStore] forceDeleteStudy failed:`, e.message);  }
}

export async function clearAll() {
  try {
    await ready();
    // [v8] Check for protected studies
    const protectedCount = await db.studies.where('protectedStudy').equals(1).count().catch(() => 0);
    if (protectedCount > 0) {
      log.warn(`[DicomStore] clearAll: skipping ${protectedCount} protected studies`);
      const unprotected = await db.studies.filter(s => !s.protectedStudy).toArray();
      for (const s of unprotected) {
        await db.files.where('studyId').equals(s.id).delete();
        const uids = s.seriesUIDs || [];
        if (uids.length) {
          await db.thumbnails.where('seriesUID').anyOf(uids).delete().catch(() => {});
          await db.annotations.where('seriesUID').anyOf(uids).delete().catch(() => {});
          await db.viewState.where('seriesUID').anyOf(uids).delete().catch(() => {});
        }
        await db.studies.delete(s.id);
      }
      _audit('clearAll', null, { preserved: protectedCount });
      return;
    }
  
    await db.transaction('rw', db.studies, db.files, db.thumbnails, db.annotations, db.viewState, async () => {
      await db.studies.clear();
      await db.files.clear();
      await db.thumbnails.clear();
      await db.annotations.clear();
      await db.viewState.clear();
    });
    _audit('clearAll');
  } catch (e) { log.warn(`[DicomStore] clearAll failed:`, e.message);  }
}

/**
 * [v8] Полная очистка (включая protected)
 */
export async function forceClearAll() {
  try {
    await ready();
    await db.transaction('rw', db.studies, db.files, db.thumbnails, db.annotations, db.viewState, async () => {
      await db.studies.clear();
      await db.files.clear();
      await db.thumbnails.clear();
      await db.annotations.clear();
      await db.viewState.clear();
    });
    _audit('forceClearAll');
  } catch (e) { log.warn(`[DicomStore] forceClearAll failed:`, e.message);  }
}

// ══════════════════════════════════════════════════════════════
// STUDY METADATA MUTATIONS (v8)
// ══════════════════════════════════════════════════════════════

/**
 * [v8] Пометить как избранное / снять отметку
 */
export async function toggleFavourite(studyId) {
  try {
    await ready();
    const study = await db.studies.get(studyId);
    if (!study) throw new Error(`Study not found: ${studyId}`);
    const newVal = !study.favourite;
    await db.studies.update(studyId, { favourite: newVal });
    _audit('toggleFavourite', studyId, { favourite: newVal });
    return newVal;
  } catch (e) { log.warn(`[DicomStore] toggleFavourite failed:`, e.message); throw e; }
}

/**
 * [v8] Защитить исследование от удаления
 */
export async function setStudyProtected(studyId, isProtected = true) {
  try {
    await ready();
    await db.studies.update(studyId, { protectedStudy: isProtected });
    _audit('setProtected', studyId, { protectedStudy: isProtected });
  } catch (e) { log.warn(`[DicomStore] setStudyProtected failed:`, e.message);  }
}

/**
 * [v8] Заметки к исследованию
 */
export async function setStudyNotes(studyId, notes) {
  try {
    await ready();
    await db.studies.update(studyId, { notes: notes || '' });
    _audit('setNotes', studyId);
  } catch (e) { log.warn(`[DicomStore] setStudyNotes failed:`, e.message);  }
}

/**
 * [v8] Получить заметки
 */
export async function getStudyNotes(studyId) {
  try {
    await ready();
    const study = await db.studies.get(studyId);
    return study?.notes || '';
  } catch (e) { log.warn(`[DicomStore] getStudyNotes failed:`, e.message); return null; }
}

/**
 * [v8] Массовое добавление тегов
 */
export async function bulkAddTags(studyIds, newTags) {
  try {
    await ready();
    let modified = 0;
    for (const id of studyIds) {
      const study = await db.studies.get(id);
      if (!study) continue;
      const merged = [...new Set([...(study.tags || []), ...newTags])];
      await db.studies.update(id, { tags: merged });
      modified++;
    }
    _audit('bulkAddTags', null, { count: modified, tags: newTags });
    return modified;
  } catch (e) { log.warn(`[DicomStore] bulkAddTags failed:`, e.message); return 0; }
}

/**
 * [v8] Массовое удаление тегов
 */
export async function bulkRemoveTags(studyIds, tagsToRemove) {
  try {
    await ready();
    let modified = 0;
    for (const id of studyIds) {
      const study = await db.studies.get(id);
      if (!study) continue;
      const filtered = (study.tags || []).filter(t => !tagsToRemove.includes(t));
      await db.studies.update(id, { tags: filtered });
      modified++;
    }
    _audit('bulkRemoveTags', null, { count: modified, tags: tagsToRemove });
    return modified;
  } catch (e) { log.warn(`[DicomStore] bulkRemoveTags failed:`, e.message); return 0; }
}

/**
 * [v8] Получить все уникальные теги
 */
export async function getAllTags() {
  try {
    await ready();
    const studies = await db.studies.toArray();
    const tagSet = new Set();
    for (const s of studies) (s.tags || []).forEach(t => tagSet.add(t));
    return [...tagSet].sort();
  } catch (e) { log.warn(`[DicomStore] getAllTags failed:`, e.message); return []; }
}

/**
 * [v8] Получить избранные исследования
 */
export async function getFavourites() {
  try {
    await ready();
    return db.studies.filter(s => !!s.favourite).toArray();
  } catch (e) { log.warn(`[DicomStore] getFavourites failed:`, e.message); return []; }
}

/**
 * [v8] Получить защищённые исследования
 */
export async function getProtectedStudies() {
  try {
    await ready();
    return db.studies.filter(s => !!s.protectedStudy).toArray();
  } catch (e) { log.warn(`[DicomStore] getProtectedStudies failed:`, e.message); return []; }
}

/**
 * [v8] Дубликация исследования (клон метаданных + файлов)
 */
export async function duplicateStudy(studyId, newId) {
  try {
    await ready();
    const study = await db.studies.get(studyId);
    if (!study) throw new Error(`Study not found: ${studyId}`);
  
    const cloneId = newId || `${studyId}_copy_${Date.now()}`;
    const cloneMeta = { ...study, id: cloneId, savedAt: new Date().toISOString(), lastAccessed: Date.now(), notes: `(Копия) ${study.notes || ''}` };
    await db.studies.put(cloneMeta);
  
    const records = await db.files.where('studyId').equals(studyId).toArray();
    if (records.length) {
      const cloneRecords = records.map(r => ({ studyId: cloneId, name: r.name, data: r.data }));
      for (let i = 0; i < cloneRecords.length; i += SAVE_BATCH_SIZE) {
        await db.files.bulkAdd(cloneRecords.slice(i, i + SAVE_BATCH_SIZE));
        await yieldThread();
      }
    }
  
    _audit('duplicateStudy', studyId, { cloneId });
    return cloneMeta;
  } catch (e) { log.warn(`[DicomStore] duplicateStudy failed:`, e.message); throw e; }
}

/**
 * [v8] Обновить произвольные поля мета-данных
 */
export async function updateStudyMeta(studyId, fields) {
  try {
    await ready();
    // Запрещаем менять id и fileCount напрямую
    const safe = { ...fields };
    delete safe.id;
    delete safe.fileCount;
    await db.studies.update(studyId, safe);
    _audit('updateMeta', studyId, { fields: Object.keys(safe) });
  } catch (e) { log.warn(`[DicomStore] updateStudyMeta failed:`, e.message);  }
}

// ══════════════════════════════════════════════════════════════
// ANNOTATIONS API (v5+)
// ══════════════════════════════════════════════════════════════

export async function saveAnnotations(seriesUID, jsonData, studyId = null) {
  try {
    await ready();
    await db.annotations.put({
      seriesUID, studyId, data: jsonData,
      savedAt: Date.now(), version: CURRENT_DB_VERSION,
    });
    _audit('saveAnnotations', studyId, { seriesUID });
  } catch (e) { log.warn(`[DicomStore] saveAnnotations failed:`, e.message);  }
}

export async function loadAnnotations(seriesUID) {
  try {
    await ready();
    const rec = await db.annotations.get(seriesUID);
    return rec?.data || null;
  } catch (e) { log.warn(`[DicomStore] loadAnnotations failed:`, e.message); return null; }
}

export async function deleteAnnotations(seriesUID) {
  try {
    await ready();
    await db.annotations.delete(seriesUID);
    _audit('deleteAnnotations', null, { seriesUID });
  } catch (e) { log.warn(`[DicomStore] deleteAnnotations failed:`, e.message);  }
}

export async function getAllAnnotations() {
  try {
    await ready();
    return db.annotations.toArray();
  } catch (e) { log.warn(`[DicomStore] getAllAnnotations failed:`, e.message); return []; }
}

/**
 * [v8] Подсчёт аннотаций по типам
 */
export async function getAnnotationStats() {
  try {
    await ready();
    const all = await db.annotations.toArray();
    const byStudy = {};
    let totalMeasurements = 0;
    for (const a of all) {
      const sid = a.studyId || 'unknown';
      byStudy[sid] = (byStudy[sid] || 0) + 1;
      // Считаем измерения внутри data
      try {
        const parsed = typeof a.data === 'string' ? JSON.parse(a.data) : a.data;
        if (Array.isArray(parsed)) totalMeasurements += parsed.length;
        else if (parsed?.annotations) totalMeasurements += Object.values(parsed.annotations).flat().length;
      } catch (e) { log.debug("[dicomStore]", e.message); }
    }
    return { total: all.length, byStudy, totalMeasurements };
  } catch (e) { log.warn(`[DicomStore] getAnnotationStats failed:`, e.message); return {}; }
}

// ══════════════════════════════════════════════════════════════
// VIEW STATE API (v6+)
// ══════════════════════════════════════════════════════════════

export async function saveViewState(seriesUID, state) {
  try {
    await ready();
    await db.viewState.put({ seriesUID, ...state, savedAt: Date.now() });
  } catch (e) { log.warn(`[DicomStore] saveViewState failed:`, e.message);  }
}

export async function loadViewState(seriesUID) {
  try {
    await ready();
    return (await db.viewState.get(seriesUID)) || null;
  } catch (e) { log.warn(`[DicomStore] loadViewState failed:`, e.message); return null; }
}

/**
 * [v8] Получить все ViewState
 */
export async function getAllViewStates() {
  try {
    await ready();
    return db.viewState.toArray();
  } catch (e) { log.warn(`[DicomStore] getAllViewStates failed:`, e.message); return []; }
}

/**
 * [v8] Удалить ViewState для серии
 */
export async function deleteViewState(seriesUID) {
  try {
    await ready();
    await db.viewState.delete(seriesUID);
  } catch (e) { log.warn(`[DicomStore] deleteViewState failed:`, e.message);  }
}

/**
 * [v8] Очистить все ViewState
 */
export async function clearAllViewStates() {
  try {
    await ready();
    await db.viewState.clear();
    _audit('clearAllViewStates');
  } catch (e) { log.warn(`[DicomStore] clearAllViewStates failed:`, e.message);  }
}

// ══════════════════════════════════════════════════════════════
// USER PREFS API (v5+)
// ══════════════════════════════════════════════════════════════

export async function getUserPref(key) {
  try {
    await ready();
    const rec = await db.userPrefs.get(key);
    return rec?.value ?? null;
  } catch (e) { log.warn(`[DicomStore] getUserPref failed:`, e.message); return null; }
}

export async function setUserPref(key, value) {
  try {
    await ready();
    await db.userPrefs.put({ key, value });
  } catch (e) { log.warn(`[DicomStore] setUserPref failed:`, e.message);  }
}

export async function getAllUserPrefs() {
  try {
    await ready();
    const all = await db.userPrefs.toArray();
    const prefs = {};
    for (const p of all) prefs[p.key] = p.value;
    return prefs;
  } catch (e) { log.warn(`[DicomStore] getAllUserPrefs failed:`, e.message); return {}; }
}

/**
 * [v8] Удалить пользовательскую настройку
 */
export async function deleteUserPref(key) {
  try {
    await ready();
    await db.userPrefs.delete(key);
  } catch (e) { log.warn(`[DicomStore] deleteUserPref failed:`, e.message);  }
}

/**
 * [v8] Сбросить все пользовательские настройки
 */
export async function resetAllUserPrefs() {
  try {
    await ready();
    await db.userPrefs.clear();
    await db.userPrefs.bulkPut([
      { key: 'profile', value: 'general' },
      { key: 'dbVersion', value: CURRENT_DB_VERSION },
    ]);
    _audit('resetAllUserPrefs');
  } catch (e) { log.warn(`[DicomStore] resetAllUserPrefs failed:`, e.message);  }
}

// ══════════════════════════════════════════════════════════════
// THUMBNAILS CACHE
// ══════════════════════════════════════════════════════════════

export async function saveThumbnail(seriesUID, imageData, источник = '') {
  try {
    await ready();
    // [11.0.3] Рядом с картинкой пишем, КЕМ она сделана. Повод: превью,
    // снятое сломанным способом (чёрный квадрат) или снятое с экрана,
    // неотличимо от хорошего — и код, увидев запись, пропускал серию навсегда.
    // Метка позволяет пересобрать устаревшие, не стирая всё подряд.
    await db.thumbnails.put({ seriesUID, imageData, источник, savedAt: Date.now() });
  } catch (e) { log.warn(`[DicomStore] saveThumbnail failed:`, e.message);  }
}

/** [11.0.3] Чем сделано превью — для решения, пересобирать ли его. */
export async function getThumbnailSource(seriesUID) {
  try {
    await ready();
    const rec = await db.thumbnails.get(seriesUID);
    return rec ? (rec.источник || '') : null;   // null — записи нет вовсе
  } catch (e) { log.warn(`[DicomStore] getThumbnailSource failed:`, e.message); return null; }
}

export async function getThumbnail(seriesUID) {
  try {
    await ready();
    const rec = await db.thumbnails.get(seriesUID);
    return rec?.imageData || null;
  } catch (e) { log.warn(`[DicomStore] getThumbnail failed:`, e.message); return null; }
}

export async function getAllThumbnails() {
  try {
    await ready();
    const all = await db.thumbnails.toArray();
    return new Map(all.map(r => [r.seriesUID, r.imageData]));
  } catch (e) { log.warn(`[DicomStore] getAllThumbnails failed:`, e.message); return new Map(); }
}

/**
 * [v8] Очистить кэш превью
 */
export async function clearThumbnails() {
  try {
    await ready();
    const count = await db.thumbnails.count();
    await db.thumbnails.clear();
    _audit('clearThumbnails', null, { count });
    return count;
  } catch (e) { log.warn(`[DicomStore] clearThumbnails failed:`, e.message); return null; }
}

/**
 * [v8] Получить размер кэша превью
 */
export async function getThumbnailStats() {
  try {
    await ready();
    const all = await db.thumbnails.toArray();
    let totalBytes = 0;
    for (const t of all) {
      totalBytes += (t.imageData?.length || 0) * 0.75; // approx Base64 decode ratio
    }
    return { count: all.length, estimatedMB: +(totalBytes / 1048576).toFixed(1) };
  } catch (e) { log.warn(`[DicomStore] getThumbnailStats failed:`, e.message); return {}; }
}

// ══════════════════════════════════════════════════════════════
// QUALITY ANALYSIS API
// ══════════════════════════════════════════════════════════════

export async function getStudyQuality(studyId) {
  try {
    await ready();
    const study = await db.studies.get(studyId);
    if (!study) throw new Error(`Study not found: ${studyId}`);
    if (study.qualityParams) return StudyQualityAnalyzer.analyze(study.qualityParams);
  
    const files = await db.files.where('studyId').equals(studyId).limit(1).toArray();
    if (!files.length) throw new Error('No files in study');
    const params = await _parseArrayBuffer(files[0].data);
    await db.studies.update(studyId, { qualityParams: params });
    return StudyQualityAnalyzer.analyze(params);
  } catch (e) { log.warn(`[DicomStore] getStudyQuality failed:`, e.message); throw e; }
}

export function analyzeCurrentImage(imageId, metaDataProvider) {
  return StudyQualityAnalyzer.analyze(
    StudyQualityAnalyzer.extractFromCornerstoneMetaData(imageId, metaDataProvider)
  );
}

export function analyzeFromDataSet(dataSet) {
  return StudyQualityAnalyzer.analyze(StudyQualityAnalyzer.extract(dataSet));
}

export async function getStudyQualityHTML(studyId) {
  try {
    return StudyQualityAnalyzer.renderHTML(await getStudyQuality(studyId));
  } catch (e) { log.warn(`[DicomStore] getStudyQualityHTML failed:`, e.message); return null; }
}

export function getCurrentImageQualityHTML(imageId, metaDataProvider) {
  return StudyQualityAnalyzer.renderHTML(analyzeCurrentImage(imageId, metaDataProvider));
}

/**
 * [v8] Пакетный анализ качества всех исследований
 */
export async function batchAnalyzeQuality(onProgress) {
  try {
    await ready();
    const studies = await db.studies.toArray();
    const results = [];
    for (let i = 0; i < studies.length; i++) {
      try {
        const q = await getStudyQuality(studies[i].id);
        results.push({ studyId: studies[i].id, patientName: studies[i].patientName, quality: q });
      } catch (e) {
        results.push({ studyId: studies[i].id, patientName: studies[i].patientName, error: e.message });
      }
      if (onProgress) onProgress(i + 1, studies.length);
      await yieldThread();
    }
    return results;
  } catch (e) { log.warn(`[DicomStore] batchAnalyzeQuality failed:`, e.message); return null; }
}

/**
 * [v8] Сравнить качество двух исследований
 */
export async function compareStudyQuality(studyId1, studyId2) {
  try {
    const [q1, q2] = await Promise.all([
      getStudyQuality(studyId1),
      getStudyQuality(studyId2),
    ]);
    return {
      study1: { id: studyId1, ...q1 },
      study2: { id: studyId2, ...q2 },
      comparison: {
        betterDose: (q1.dose?.effectiveDose ?? 999) < (q2.dose?.effectiveDose ?? 999) ? studyId1 : studyId2,
        betterResolution: (q1.params?.sliceThickness ?? 999) < (q2.params?.sliceThickness ?? 999) ? studyId1 : studyId2,
        betterNoise: q1.noiseLevel === 'low' ? studyId1 : q2.noiseLevel === 'low' ? studyId2 : 'equal',
      },
    };
  } catch (e) { log.warn(`[DicomStore] compareStudyQuality failed:`, e.message); return null; }
}

// ══════════════════════════════════════════════════════════════
// STATS
// ══════════════════════════════════════════════════════════════

export async function getStats() {
  try {
    await ready();
    const [studyCount, fileCount, annCount, studies] = await Promise.all([
      db.studies.count(),
      db.files.count(),
      db.annotations.count(),
      db.studies.toArray(),
    ]);
  
    const totalSize = studies.reduce((s, st) => s + (st.sizeBytes || 0), 0);
    const byModality = {};
    for (const st of studies) {
      const mod = st.modality || 'OTHER';
      byModality[mod] = (byModality[mod] || 0) + 1;
    }
    const patients = new Set(studies.map(s => s.patientId || s.patientName || 'Unknown'));
  
    // [v8] Extra stats
    const favouriteCount = studies.filter(s => s.favourite).length;
    const protectedCount = studies.filter(s => s.protectedStudy).length;
    const taggedCount = studies.filter(s => s.tags?.length > 0).length;
    const withNotesCount = studies.filter(s => s.notes).length;
  
    return {
      dbVersion: CURRENT_DB_VERSION,
      studyCount, fileCount, annotationCount: annCount,
      totalSize, totalSizeMB: +(totalSize / 1048576).toFixed(1),
      byModality,
      patientCount: patients.size,
      favouriteCount, protectedCount, taggedCount, withNotesCount,
      oldestStudy: studies.reduce((o, s) => (!o || s.savedAt < o ? s.savedAt : o), null),
      newestStudy: studies.reduce((n, s) => (!n || s.savedAt > n ? s.savedAt : n), null),
      evictionPolicy: _evictionPolicy,
    };
  } catch (e) { log.warn(`[DicomStore] getStats failed:`, e.message); return {}; }
}

export async function getStorageEstimate() {
  try {
    if (navigator.storage?.estimate) {
      const est = await navigator.storage.estimate();
      return {
        used: est.usage || 0,
        quota: est.quota || 0,
        percent: est.quota ? Math.round((est.usage / est.quota) * 100) : 0,
        gbUsed: ((est.usage || 0) / (1024 ** 3)).toFixed(2) + ' GB',
        gbQuota: ((est.quota || 0) / (1024 ** 3)).toFixed(2) + ' GB',
      };
    }
    return { used: 0, quota: 0, percent: 0, gbUsed: '0 GB', gbQuota: '0 GB' };
  } catch (e) { log.warn(`[DicomStore] getStorageEstimate failed:`, e.message); return {}; }
}

// ══════════════════════════════════════════════════════════════
// TRUE LRU CLEANUP + INTEGRITY (v7) + EVICTION POLICIES (v8)
// ══════════════════════════════════════════════════════════════

/**
 * [v7+v8] Configurable eviction with policy support.
 */
async function _autoCleanup(force = false) {
  try {
    const count = await db.studies.count();
    if (!force && count <= MAX_STUDIES) {
      const est = await getStorageEstimate();
      if (est.percent < STORAGE_LIMIT_PERCENT) return;
    }

    const excess = count > MAX_STUDIES ? count - MAX_STUDIES : 1;
    const allStudies = await db.studies.toArray();
    const policyFn = EVICTION_POLICIES[_evictionPolicy] || EVICTION_POLICIES.protectedLru;
    const toEvict = policyFn(allStudies, excess);

    for (const s of toEvict) {
      log.info(`[DicomStore] Eviction (${_evictionPolicy}): "${s.patientName}" (last: ${new Date(s.lastAccessed || 0).toLocaleString()})`);
      // Bypass protected check for auto-cleanup when using non-protected policy
      if (s.protectedStudy && _evictionPolicy !== 'protectedLru') {
        await db.studies.update(s.id, { protectedStudy: false });
      }
      try { await deleteStudy(s.id); } catch { /* protected — skip */ }
    }
  } catch (e) { log.warn('[DicomStore] Auto-cleanup error:', e.message); }
}

/**
 * [v8] Установить политику вытеснения
 */
export function setEvictionPolicy(policy) {
  if (!EVICTION_POLICIES[policy]) throw new Error(`Unknown policy: ${policy}. Available: ${Object.keys(EVICTION_POLICIES).join(', ')}`);
  _evictionPolicy = policy;
  log.info(`[DicomStore] Eviction policy → ${policy}`);
  return policy;
}

/**
 * [v8] Получить текущую политику
 */
export function getEvictionPolicy() {
  return { current: _evictionPolicy, available: Object.keys(EVICTION_POLICIES) };
}

export async function setStorageLimits({ maxStudies, limitPercent } = {}) {
  try {
    await ready();
    if (maxStudies != null) await db.settings.put({ key: 'maxStudies', value: maxStudies });
    if (limitPercent != null) await db.settings.put({ key: 'limitPercent', value: limitPercent });
    _audit('setStorageLimits', null, { maxStudies, limitPercent });
  } catch (e) { log.warn(`[DicomStore] setStorageLimits failed:`, e.message);  }
}

/**
 * [v7] Проверка целостности — находит исследования с потерянными файлами
 */
export async function verifyStudyIntegrity(studyId) {
  try {
    await ready();
    const meta = await db.studies.get(studyId);
    if (!meta) return { valid: false, error: 'Meta not found' };
  
    const filesCount = await db.files.where('studyId').equals(studyId).count();
    const isValid = filesCount === meta.fileCount;
  
    return {
      valid: isValid,
      expected: meta.fileCount,
      actual: filesCount,
      needsRepair: !isValid && filesCount === 0,
      partial: !isValid && filesCount > 0 && filesCount < meta.fileCount,
    };
  } catch (e) { log.warn(`[DicomStore] verifyStudyIntegrity failed:`, e.message); return null; }
}

/**
 * [v7] Проверка всех исследований — возвращает список битых
 */
export async function verifyAllStudies() {
  try {
    await ready();
    const studies = await db.studies.toArray();
    const broken = [];
    for (const s of studies) {
      const result = await verifyStudyIntegrity(s.id);
      if (!result.valid) broken.push({ id: s.id, patientName: s.patientName, ...result });
    }
    return broken;
  } catch (e) { log.warn(`[DicomStore] verifyAllStudies failed:`, e.message); return []; }
}

/**
 * [v8] Авто-ремонт: обновляет fileCount в мета исходя из реальных файлов
 */
export async function repairStudyIntegrity(studyId) {
  try {
    await ready();
    const meta = await db.studies.get(studyId);
    if (!meta) throw new Error(`Study not found: ${studyId}`);
  
    const actualCount = await db.files.where('studyId').equals(studyId).count();
    if (actualCount === meta.fileCount) return { repaired: false, message: 'Already consistent' };
  
    await db.studies.update(studyId, { fileCount: actualCount });
    _audit('repairIntegrity', studyId, { was: meta.fileCount, now: actualCount });
    return { repaired: true, was: meta.fileCount, now: actualCount };
  } catch (e) { log.warn(`[DicomStore] repairStudyIntegrity failed:`, e.message); throw e; }
}

/**
 * [v8] Пакетный ремонт всех исследований
 */
export async function repairAllStudies() {
  try {
    await ready();
    const studies = await db.studies.toArray();
    let repaired = 0;
    for (const s of studies) {
      const r = await repairStudyIntegrity(s.id);
      if (r.repaired) repaired++;
      await yieldThread();
    }
    _audit('repairAll', null, { repaired });
    return { total: studies.length, repaired };
  } catch (e) { log.warn(`[DicomStore] repairAllStudies failed:`, e.message); return {}; }
}

/**
 * [v8] Vacuum — удаляет осиротевшие файлы (files без study)
 */
export async function vacuum() {
  try {
    await ready();
    const studyIds = new Set((await db.studies.toArray()).map(s => s.id));
    const allFiles = await db.files.toArray();
    const orphans = allFiles.filter(f => !studyIds.has(f.studyId));
  
    if (orphans.length) {
      const orphanIds = orphans.map(f => f.id);
      for (let i = 0; i < orphanIds.length; i += 100) {
        await db.files.bulkDelete(orphanIds.slice(i, i + 100));
        await yieldThread();
      }
    }
  
    // Orphaned thumbnails
    const seriesUIDs = new Set();
    (await db.studies.toArray()).forEach(s => (s.seriesUIDs || []).forEach(u => seriesUIDs.add(u)));
    const allThumbs = await db.thumbnails.toArray();
    const orphanThumbs = allThumbs.filter(t => !seriesUIDs.has(t.seriesUID));
    if (orphanThumbs.length) {
      await db.thumbnails.bulkDelete(orphanThumbs.map(t => t.seriesUID));
    }
  
    _audit('vacuum', null, { orphanFiles: orphans.length, orphanThumbs: orphanThumbs.length });
    return { orphanFilesRemoved: orphans.length, orphanThumbsRemoved: orphanThumbs.length };
  } catch (e) { log.warn(`[DicomStore] vacuum failed:`, e.message); return null; }
}

// ══════════════════════════════════════════════════════════════
// EXPORT / IMPORT (OOM-Safe) + v8 selective export
// ══════════════════════════════════════════════════════════════

export async function exportStudy(studyId, options = {}) {
  try {
    await ready();
    const meta = await db.studies.get(studyId);
    if (!meta) throw new Error(`Study not found: ${studyId}`);
  
    const { includeFiles = true, includeAnnotations = true, includeViewStates = true } = options;
  
    let filesExport = [];
    if (includeFiles) {
      const records = await db.files.where('studyId').equals(studyId).toArray();
      for (const r of records) {
        filesExport.push({
          name: r.name,
          data: await _bufferToBase64Native(r.data),
        });
        await yieldThread();
      }
    }
  
    const annotations = [], viewStates = [];
    if (meta.seriesUIDs?.length) {
      for (const uid of meta.seriesUIDs) {
        if (includeAnnotations) {
          const ann = await db.annotations.get(uid); if (ann) annotations.push(ann);
        }
        if (includeViewStates) {
          const vs = await db.viewState.get(uid); if (vs) viewStates.push(vs);
        }
      }
    }
  
    _audit('exportStudy', studyId, { includeFiles, includeAnnotations, includeViewStates });
  
    return new Blob([JSON.stringify({
      version: EXPORT_FORMAT_VERSION,
      dbVersion: CURRENT_DB_VERSION,
      exportedAt: new Date().toISOString(),
      meta, files: filesExport, annotations, viewStates,
    })], { type: 'application/json' });
  } catch (e) { log.warn(`[DicomStore] exportStudy failed:`, e.message); throw e; }
}

export async function importStudy(blob, onProgress) {
  try {
    await ready();
    const text = await blob.text();
    const data = JSON.parse(text);
    if (!data.version || !data.meta) throw new Error('Invalid export format');
  
    const studyMeta = { ...data.meta, savedAt: new Date().toISOString(), lastAccessed: Date.now() };
    await db.studies.put(studyMeta);
    await db.files.where('studyId').equals(studyMeta.id).delete();
  
    let saved = 0;
    const total = data.files?.length || 0;
  
    if (total > 0) {
      for (let i = 0; i < total; i += SAVE_BATCH_SIZE) {
        const batch = data.files.slice(i, i + SAVE_BATCH_SIZE);
  
        const records = await Promise.all(batch.map(async f => ({
          studyId: studyMeta.id,
          name: f.name,
          data: await _base64ToBufferNative(f.data),
        })));
  
        await db.files.bulkAdd(records);
        saved += batch.length;
        if (onProgress) onProgress(saved, total);
        await yieldThread();
      }
    }
  
    // v2+ format: annotations + viewState
    if (data.version >= 2) {
      if (data.annotations?.length) {
        await db.annotations.bulkPut(data.annotations).catch(e => log.debug('[DicomStore] import annotations:', e.message));
      }
      if (data.viewStates?.length) {
        await db.viewState.bulkPut(data.viewStates).catch(e => log.debug('[DicomStore] import viewStates:', e.message));
      }
    }
  
    _audit('importStudy', studyMeta.id, { files: total, format: data.version });
    return studyMeta;
  } catch (e) { log.warn(`[DicomStore] importStudy failed:`, e.message); throw e; }
}

/**
 * [v8] Экспорт всех исследований одним архивом
 */
export async function exportAllStudies(onProgress) {
  try {
    await ready();
    const studies = await db.studies.toArray();
    const exports = [];
    for (let i = 0; i < studies.length; i++) {
      const blob = await exportStudy(studies[i].id);
      const text = await blob.text();
      exports.push(JSON.parse(text));
      if (onProgress) onProgress(i + 1, studies.length);
      await yieldThread();
    }
    return new Blob([JSON.stringify({
      version: EXPORT_FORMAT_VERSION,
      dbVersion: CURRENT_DB_VERSION,
      exportedAt: new Date().toISOString(),
      type: 'bulk',
      studies: exports,
    })], { type: 'application/json' });
  } catch (e) { log.warn(`[DicomStore] exportAllStudies failed:`, e.message); return []; }
}

/**
 * [v8] Экспорт только аннотаций (без файлов)
 */
export async function exportAnnotationsOnly() {
  try {
    await ready();
    const annotations = await db.annotations.toArray();
    return new Blob([JSON.stringify({
      version: EXPORT_FORMAT_VERSION,
      type: 'annotations-only',
      exportedAt: new Date().toISOString(),
      annotations,
    })], { type: 'application/json' });
  } catch (e) { log.warn(`[DicomStore] exportAnnotationsOnly failed:`, e.message); return null; }
}

/**
 * [v8] Импорт только аннотаций
 */
export async function importAnnotationsOnly(blob) {
  try {
    await ready();
    const text = await blob.text();
    const data = JSON.parse(text);
    if (data.type !== 'annotations-only' || !data.annotations) throw new Error('Invalid annotations-only format');
    await db.annotations.bulkPut(data.annotations);
    _audit('importAnnotationsOnly', null, { count: data.annotations.length });
    return data.annotations.length;
  } catch (e) { log.warn(`[DicomStore] importAnnotationsOnly failed:`, e.message); throw e; }
}

// ══════════════════════════════════════════════════════════════
// QUOTA WATCHDOG (v8)
// ══════════════════════════════════════════════════════════════

let _quotaWatchdogInterval = null;
const _quotaCallbacks = new Set();

/**
 * [v8] Запустить мониторинг квоты. callback вызывается при превышении threshold%.
 */
export function startQuotaWatchdog(thresholdPercent = 80, callback) {
  stopQuotaWatchdog();
  if (callback) _quotaCallbacks.add(callback);

  _quotaWatchdogInterval = setInterval(async () => {
    try {
      const est = await getStorageEstimate();
      if (est.percent >= thresholdPercent) {
        for (const cb of _quotaCallbacks) {
          try { cb(est); } catch (e) { log.warn('[DicomStore] Quota callback error:', e.message); }
        }
      }
    } catch (e) { log.warn('[DicomStore] Quota check failed:', e.message); }
  }, QUOTA_CHECK_INTERVAL_MS);
  log.info(`[DicomStore] Quota watchdog started (${thresholdPercent}%)`);
}

/**
 * [v8] Остановить мониторинг
 */
export function stopQuotaWatchdog() {
  if (_quotaWatchdogInterval) {
    clearInterval(_quotaWatchdogInterval);
    _quotaWatchdogInterval = null;
  }
  _quotaCallbacks.clear();
}

/**
 * [v8] Статус watchdog
 */
export function getQuotaWatchdogState() {
  return { active: !!_quotaWatchdogInterval, callbacks: _quotaCallbacks.size };
}

// ══════════════════════════════════════════════════════════════
// MEMORY-SAFE HELPERS
// ══════════════════════════════════════════════════════════════

/** Native ArrayBuffer → Base64 через FileReader (no Call Stack overflow) */
function _bufferToBase64Native(buffer) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(new Blob([buffer]));
  });
}

/** Native Base64 → ArrayBuffer через Fetch API */
async function _base64ToBufferNative(base64) {
  const res = await fetch(`data:application/octet-stream;base64,${base64}`);
  return res.arrayBuffer();
}

async function _extractQualityFromFile(file) {
  const arrayBuffer = file instanceof ArrayBuffer ? file : await file.arrayBuffer();
  return _parseArrayBuffer(arrayBuffer);
}

async function _parseArrayBuffer(arrayBuffer) {
  let dicomParser;
  try {
    dicomParser = (await import('dicom-parser')).default || (await import('dicom-parser'));
  } catch (e) { log.debug('[DicomStore] dicom-parser import fallback:', e.message); }
  if (!dicomParser) {
    try {
      dicomParser = (await import('@cornerstonejs/dicom-image-loader')).external?.dicomParser;
    } catch (e) { log.debug('[DicomStore] cornerstoneDICOM fallback:', e.message); }
    if (!dicomParser) dicomParser = window.dicomParser;
  }
  if (!dicomParser) { log.warn('[DicomStore] dicomParser not available'); return null; }
  const dataSet = dicomParser.parseDicom(new Uint8Array(arrayBuffer));
  return StudyQualityAnalyzer.extract(dataSet);
}

// ══════════════════════════════════════════════════════════════
// EXPORTS
// ══════════════════════════════════════════════════════════════

export { StudyQualityAnalyzer } from './studyQualityAnalyzer.js';
export { db };
